import { useState, useEffect, useCallback } from 'react';
import API from '@/lib/api';
import { Plus, PencilSimple, Trash, Truck, Phone, EnvelopeSimple } from '@phosphor-icons/react';

const EMPTY = {
  naam: '', adres: '', postcode: '', stad: '', land: 'België',
  tel: '', email: '', btw_nr: '',
  vervoerderslicentie: '', standaard_kenteken: '', standaard_chauffeur: '',
  opmerkingen: '', actief: true,
};

export default function VervoerdersPage() {
  const [items, setItems] = useState([]);
  const [zoek, setZoek] = useState('');
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const r = await API.get('/vervoerders');
    setItems(r.data || []);
  }, []);

  useEffect(() => { load(); }, [load]);

  const u = (key) => (e) => setForm(p => ({ ...p, [key]: e.target.value }));

  const openNieuw = () => {
    setForm({ ...EMPTY });
    setEditId(null);
    setShowForm(true);
  };

  const openEdit = (v) => {
    setForm({ ...EMPTY, ...v });
    setEditId(v.id);
    setShowForm(true);
  };

  const save = async () => {
    if (!form.naam.trim()) {
      alert('Naam is verplicht');
      return;
    }
    setSaving(true);
    try {
      if (editId) {
        await API.put(`/vervoerders/${editId}`, form);
      } else {
        await API.post('/vervoerders', form);
      }
      setShowForm(false);
      setForm(EMPTY);
      setEditId(null);
      load();
    } catch (e) {
      alert('Opslaan mislukt: ' + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (v) => {
    if (!window.confirm(`Vervoerder "${v.naam}" verwijderen?`)) return;
    try {
      await API.delete(`/vervoerders/${v.id}`);
      load();
    } catch (e) {
      alert('Verwijderen mislukt: ' + (e.response?.data?.detail || e.message));
    }
  };

  const filtered = items.filter(v => {
    if (!zoek.trim()) return true;
    const q = zoek.toLowerCase();
    return (v.naam || '').toLowerCase().includes(q) ||
           (v.btw_nr || '').toLowerCase().includes(q) ||
           (v.stad || '').toLowerCase().includes(q) ||
           (v.standaard_kenteken || '').toLowerCase().includes(q);
  });

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 inline-flex items-center gap-2">
            <Truck size={28} className="text-[#002FA7]" />
            Vervoerders
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Beheer transporteurs voor CMR-vrachtbrieven ({items.length} geregistreerd)
          </p>
        </div>
        <button
          className="btn btn-primary inline-flex items-center gap-1.5"
          onClick={openNieuw}
          data-testid="vervoerder-nieuw-btn"
        >
          <Plus size={16} /> Nieuwe vervoerder
        </button>
      </div>

      {/* Zoekbalk */}
      <div className="mb-3">
        <input
          className="field-input"
          style={{ maxWidth: 400 }}
          placeholder="Zoek op naam, BTW, stad of kenteken..."
          value={zoek}
          onChange={e => setZoek(e.target.value)}
          data-testid="vervoerder-zoek"
        />
      </div>

      {/* Tabel */}
      <div className="bg-white border border-gray-200 overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider text-gray-600">Naam</th>
              <th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider text-gray-600">BTW / Licentie</th>
              <th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider text-gray-600">Stad / Land</th>
              <th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider text-gray-600">Contact</th>
              <th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider text-gray-600">Std. voertuig</th>
              <th className="px-3 py-2 text-right text-[10px] uppercase tracking-wider text-gray-600">Acties</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={6} className="px-3 py-6 text-center text-gray-400">
                {items.length === 0 ? 'Nog geen vervoerders. Klik op "Nieuwe vervoerder" om te beginnen.' : 'Geen resultaten voor uw zoekopdracht.'}
              </td></tr>
            )}
            {filtered.map(v => (
              <tr key={v.id} className="border-b border-gray-100 hover:bg-[#F0F4FF] cursor-pointer" onClick={() => openEdit(v)} data-testid={`vervoerder-row-${v.id}`}>
                <td className="px-3 py-2">
                  <div className="font-bold text-gray-900">{v.naam}</div>
                  {!v.actief && <span className="inline-block px-1.5 py-0.5 bg-gray-200 text-[10px] uppercase">Inactief</span>}
                </td>
                <td className="px-3 py-2 font-mono text-[11px]">
                  <div>{v.btw_nr || '—'}</div>
                  {v.vervoerderslicentie && <div className="text-gray-500">Lic: {v.vervoerderslicentie}</div>}
                </td>
                <td className="px-3 py-2 text-gray-700">
                  {v.stad ? <div>{v.postcode} {v.stad}</div> : <span className="text-gray-400">—</span>}
                  <div className="text-[10px] text-gray-500">{v.land}</div>
                </td>
                <td className="px-3 py-2 text-[11px]">
                  {v.tel && <div className="flex items-center gap-1"><Phone size={10} /> <a href={`tel:${v.tel}`} onClick={e => e.stopPropagation()} className="text-[#002FA7]">{v.tel}</a></div>}
                  {v.email && <div className="flex items-center gap-1"><EnvelopeSimple size={10} /> <a href={`mailto:${v.email}`} onClick={e => e.stopPropagation()} className="text-[#002FA7] truncate max-w-[200px]">{v.email}</a></div>}
                  {!v.tel && !v.email && <span className="text-gray-400">—</span>}
                </td>
                <td className="px-3 py-2 font-mono text-[11px]">
                  <div>{v.standaard_kenteken || '—'}</div>
                  {v.standaard_chauffeur && <div className="text-gray-500">{v.standaard_chauffeur}</div>}
                </td>
                <td className="px-3 py-2 text-right whitespace-nowrap" onClick={e => e.stopPropagation()}>
                  <button className="btn btn-ghost btn-sm" onClick={() => openEdit(v)} title="Bewerken" data-testid={`vervoerder-edit-${v.id}`}>
                    <PencilSimple size={14} className="text-[#002FA7]" />
                  </button>
                  <button className="btn btn-ghost btn-sm" onClick={() => remove(v)} title="Verwijderen" data-testid={`vervoerder-del-${v.id}`}>
                    <Trash size={14} className="text-red-500" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal: nieuw/bewerk */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={() => setShowForm(false)}>
          <div className="w-full max-w-2xl bg-white shadow-2xl" onClick={e => e.stopPropagation()} data-testid="vervoerder-modal">
            <div className="px-5 py-3 border-b border-gray-200 bg-[#002FA7] text-white flex items-center justify-between">
              <h3 className="text-sm font-bold inline-flex items-center gap-2">
                <Truck size={18} /> {editId ? 'Vervoerder bewerken' : 'Nieuwe vervoerder'}
              </h3>
              <button onClick={() => setShowForm(false)} className="text-white hover:opacity-70">✕</button>
            </div>
            <div className="p-5 space-y-4 max-h-[80vh] overflow-y-auto">
              {/* Algemeen */}
              <div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-2 pb-1 border-b border-gray-200">Algemeen</div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2">
                    <label className="form-label">Naam *</label>
                    <input className="field-input" value={form.naam} onChange={u('naam')} placeholder="bv. Trans Europa NV" data-testid="v-form-naam" />
                  </div>
                  <div>
                    <label className="form-label">BTW-nummer</label>
                    <input className="field-input font-mono uppercase" value={form.btw_nr} onChange={u('btw_nr')} placeholder="BE0123456789" data-testid="v-form-btw" />
                  </div>
                  <div>
                    <label className="form-label">Vervoerderslicentie</label>
                    <input className="field-input font-mono" value={form.vervoerderslicentie} onChange={u('vervoerderslicentie')} placeholder="bv. TL-123456" />
                  </div>
                </div>
              </div>

              {/* Adres */}
              <div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-2 pb-1 border-b border-gray-200">Adres</div>
                <div className="grid grid-cols-4 gap-3">
                  <div className="col-span-4">
                    <label className="form-label">Straat + nr</label>
                    <input className="field-input" value={form.adres} onChange={u('adres')} placeholder="bv. Industrielaan 45" />
                  </div>
                  <div>
                    <label className="form-label">Postcode</label>
                    <input className="field-input" value={form.postcode} onChange={u('postcode')} placeholder="2800" />
                  </div>
                  <div className="col-span-2">
                    <label className="form-label">Stad</label>
                    <input className="field-input" value={form.stad} onChange={u('stad')} placeholder="Mechelen" />
                  </div>
                  <div>
                    <label className="form-label">Land</label>
                    <input className="field-input" value={form.land} onChange={u('land')} placeholder="België" />
                  </div>
                </div>
              </div>

              {/* Contact */}
              <div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-2 pb-1 border-b border-gray-200">Contact</div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="form-label">Telefoon</label>
                    <input className="field-input" value={form.tel} onChange={u('tel')} placeholder="+32 3 123 45 67" />
                  </div>
                  <div>
                    <label className="form-label">E-mail</label>
                    <input type="email" className="field-input" value={form.email} onChange={u('email')} placeholder="dispatch@transporteur.be" />
                  </div>
                </div>
              </div>

              {/* Standaard voertuig/chauffeur */}
              <div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-2 pb-1 border-b border-gray-200">
                  Standaard voertuig
                  <span className="text-[9px] font-normal normal-case ml-1">· automatisch ingevuld bij CMR (kan nog gewijzigd worden)</span>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="form-label">Kenteken</label>
                    <input className="field-input font-mono uppercase" value={form.standaard_kenteken} onChange={u('standaard_kenteken')} placeholder="1-ABC-234" />
                  </div>
                  <div>
                    <label className="form-label">Standaard chauffeur</label>
                    <input className="field-input" value={form.standaard_chauffeur} onChange={u('standaard_chauffeur')} placeholder="bv. Jan Peeters" />
                  </div>
                </div>
              </div>

              <div>
                <label className="form-label">Opmerkingen</label>
                <textarea className="field-input" rows={2} value={form.opmerkingen} onChange={u('opmerkingen')} placeholder="Interne notities, voorkeur ritten, BTW-attest opgevraagd..." />
              </div>

              <label className="inline-flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={!!form.actief} onChange={e => setForm(p => ({ ...p, actief: e.target.checked }))} />
                <span className="text-sm">Actief (zichtbaar bij vervoerders-keuze op CMR)</span>
              </label>
            </div>
            <div className="px-5 py-3 border-t border-gray-200 bg-gray-50 flex items-center justify-end gap-2">
              <button className="btn btn-secondary btn-sm" onClick={() => setShowForm(false)}>Annuleren</button>
              <button className="btn btn-primary btn-sm" onClick={save} disabled={saving} data-testid="v-form-save">
                {saving ? 'Opslaan...' : editId ? 'Bewaren' : 'Aanmaken'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
